import statuses from '@/static/constants/statuses.json';
import { AvailableTransitions } from '@/types/Common.types';
import transitions from '@/constants/transitions';
import { InternalReviewItem } from "@/components/ActiveProjects/InternalReviewTooltip";
import {
  normalizeStatusCharactersToSlugKey,
} from "@/utils/statusBarStatusAliases";

/**
 * Active Project Status Utilities
 * 
 * Provides consistent access to active project status values across different formats
 * and helper functions for status comparison and validation.
 */

// Import individual statuses using dot operator
export const VENDOR_INFORMATION_REQUESTED = statuses.ACTIVE_PROJECT_STATUSES.VENDOR_INFORMATION_REQUESTED;
export const PARTIALLY_SUBMITTED = statuses.ACTIVE_PROJECT_STATUSES.PARTIALLY_SUBMITTED;
export const UNDER_PRODUCT_DEVELOPER_REVIEW = statuses.ACTIVE_PROJECT_STATUSES.UNDER_PRODUCT_DEVELOPER_REVIEW;
export const UNDER_INTERNAL_REVIEW = statuses.ACTIVE_PROJECT_STATUSES.UNDER_INTERNAL_REVIEW;
export const UNDER_LEGAL_REVIEW = statuses.ACTIVE_PROJECT_STATUSES.UNDER_LEGAL_REVIEW;
export const PREPARE_FOR_LAUNCH = statuses.ACTIVE_PROJECT_STATUSES.PREPARE_FOR_LAUNCH;
export const READY_FOR_ORDERS = statuses.ACTIVE_PROJECT_STATUSES.READY_FOR_ORDERS;

// Export the full object for reference
export const ACTIVE_PROJECT_STATUSES = statuses.ACTIVE_PROJECT_STATUSES;
export const ACTIVE_PROJECT_STATUSES_IN_WORKFLOW_RANGE =
  statuses.ACTIVE_PROJECT_STATUSES_IN_WORKFLOW_RANGE;

/**
 * Normalize status for comparison: lowercase; whitespace and hyphen/en/em dashes → underscores.
 * Does not apply status-bar-only aliases (e.g. prepare_for_launch → ready_for_orders); those apply in `normalizeActiveProjectStatusForStepLookup`.
 * @param status - The status to normalize
 * @returns normalized status string
 */
export const normalizeStatusForComparison = (status: string): string => {
  if (!status || typeof status !== 'string') {
    return '';
  }
  return normalizeStatusCharactersToSlugKey(status);
};

/**
 * Strips parenthetical segments from status strings for compact list / tooltip display
 * (e.g. "Under Internal Review (Under Food Safety Review, ...)" -> "Under Internal Review").
 */
export const removeBracketsFromStatus = (statusValue: unknown): string => {
  if (!statusValue) return '';
  return String(statusValue).replace(/\(.*?\)/g, '').trim();
};

/**
 * Normalize status text for list/grid "Under Internal Review" display checks:
 * lowercase, underscores → spaces, trim. Does not strip parentheticals; combine with
 * {@link removeBracketsFromStatus} first when strings may include trailing clauses in parentheses.
 */
export function normalizeStatusForListInternalReviewCheck(status: unknown): string {
  if (status == null) return "";
  return String(status)
    .toLowerCase()
    .replace(/_/g, " ")
    .trim();
}

/**
 * Whether a status **display** string should be treated as Under Internal Review in list cells
 * and tooltips (looser than {@link isUnderInternalReview}, which uses canonical slug equality).
 * Strips parentheticals first, then applies the same word checks used across Active Projects,
 * Projects in Artwork, and Launched Products grids.
 */
export function isDisplayUnderInternalReview(status: unknown): boolean {
  const cleaned = removeBracketsFromStatus(status);
  const n = normalizeStatusForListInternalReviewCheck(cleaned);
  return n.includes("internal") && n.includes("review");
}

/**
 * Human-readable status for list cells / tooltips when the API sends snake_case or mixed casing
 * (e.g. `prepare_for_launch` → "Prepare For Launch"). Aligns with Active Projects display when
 * those screens receive title-cased strings from the API.
 */
export const formatVendorAssessmentStatusForListDisplay = (statusValue: unknown): string => {
  const base = removeBracketsFromStatus(statusValue);
  if (!base) return "";
  const withSpaces = String(base)
    .replace(/\u00a0/g, " ")
    .replace(/_/g, " ")
    .trim()
    .replace(/\s+/g, " ");
  if (!withSpaces) return "";
  const formatted = withSpaces
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
  // Title-casing turns "TJ" into "Tj"; keep the canonical acronym for display and palette keys.
  return formatted.replace(
    /^Final Specification Review & Approval - Tj$/i,
    "Final Specification Review & Approval - TJ"
  );
};

/**
 * Check if a status is vendor information requested (case-insensitive with space-to-underscore conversion)
 * @param status - The status to check
 * @returns boolean indicating if the status is vendor information requested
 */
export const isVendorInformationRequested = (status: string): boolean => {
  const normalizedStatus = normalizeStatusForComparison(status);
  const vendorInfoRequested = normalizeStatusForComparison(VENDOR_INFORMATION_REQUESTED);
  
  return normalizedStatus === vendorInfoRequested;
};

/**
 * Check if a vendor status is before or equal to vendor information requested
 * Statuses that come before vendor information requested should be skipped in navigation:
 * - in_progress
 * - vendor_assessment / vendor assessment
 * - vendor_approved / vendor approved
 * - awaiting_business_review / awaiting business review
 * - ready_for_tasting_panel / ready for tasting panel
 * - tasting_panel_approved / tasting panel approved
 * - vendor_information_requested / vendor information requested
 * @param status - The vendor assessment status to check
 * @returns boolean indicating if the vendor should be skipped in navigation
 */
export const shouldSkipVendorInNavigation = (status: string | null | undefined): boolean => {
  if (!status || typeof status !== 'string') {
    return false;
  }
  
  const normalizedStatus = normalizeStatusForComparison(status);
  const vendorInfoRequested = normalizeStatusForComparison(VENDOR_INFORMATION_REQUESTED);
  
  // If status equals vendor information requested, skip it
  if (normalizedStatus === vendorInfoRequested) {
    return true;
  }
  
  // Statuses that come before vendor information requested (based on workflow progression)
  const statusesBeforeVendorInfoRequested = [
    'in_progress',
    'vendor_assessment',
    'vendor_approved',
    'awaiting_business_review',
    'ready_for_tasting_panel',
    'tasting_panel_approved'
  ];
  
  return statusesBeforeVendorInfoRequested.includes(normalizedStatus);
};

/**
 * Check if a status is partially submitted (case-insensitive)
 * @param status - The status to check
 * @returns boolean indicating if the status is partially submitted
 */
export const isPartiallySubmitted = (status: string): boolean => {
  const normalizedStatus = normalizeStatusForComparison(status);
  const partiallySubmitted = normalizeStatusForComparison(PARTIALLY_SUBMITTED);
  
  return normalizedStatus === partiallySubmitted;
};

/**
 * True when vendor assessment status is Under Legal Review (API: "Under Legal Review" / `under_legal_review`).
 */
export const isUnderLegalReview = (status: string | null | undefined): boolean => {
  if (!status || typeof status !== 'string') {
    return false;
  }
  const normalizedStatus = normalizeStatusForComparison(status);
  const canonical = normalizeStatusForComparison(UNDER_LEGAL_REVIEW);

  return normalizedStatus === canonical;
};

/**
 * True when vendor assessment status is ready for orders (API may send `prepare_for_launch` or `ready_for_orders`).
 */
export const isReadyForOrders = (status: string | null | undefined): boolean => {
  if (!status || typeof status !== 'string') {
    return false;
  }
  const normalizedStatus = normalizeStatusForComparison(status);
  const prepareForLaunch = normalizeStatusForComparison(PREPARE_FOR_LAUNCH);
  const readyForOrders = normalizeStatusForComparison(READY_FOR_ORDERS);

  return normalizedStatus === prepareForLaunch || normalizedStatus === readyForOrders;
};

/**
 * Check if a status is under product developer review (case-insensitive)
 * @param status - The status to check
 * @returns boolean indicating if the status is under product developer review
 */
export const isUnderProductDeveloperReview = (status: string): boolean => {
  const normalizedStatus = normalizeStatusForComparison(status);
  const underProductDeveloperReview = normalizeStatusForComparison(UNDER_PRODUCT_DEVELOPER_REVIEW);
  
  return normalizedStatus === underProductDeveloperReview;
};

/**
 * Check if a status is under internal review (case-insensitive)
 * @param status - The status to check
 * @returns boolean indicating if the status is under internal review
 */
export const isUnderInternalReview = (status: string): boolean => {
  const normalizedStatus = normalizeStatusForComparison(status);
  const underInternalReview = normalizeStatusForComparison(UNDER_INTERNAL_REVIEW);
  
  return normalizedStatus === underInternalReview;
};

/**
 * Check if a status should show share for review button in compliance documents
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if share for review should be shown
 */
export const shouldShowShareForReview = (businessStatus: string, workflowStatus: string[]): boolean => {
  const isCorrectBusinessStatus = isVendorInformationRequested(businessStatus) || 
                                 isPartiallySubmitted(businessStatus);
  const hasComplianceDocumentRequested = workflowStatus.includes('compliance_document_requested');
  
  return isCorrectBusinessStatus && hasComplianceDocumentRequested;
};

/**
 * Check if a status should show share for review button in packaging and labelling
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if share for review should be shown
 */
export const shouldShowShareForReviewPackaging = (businessStatus: string, workflowStatus: string[]): boolean => {
  const isCorrectBusinessStatus = isVendorInformationRequested(businessStatus) || 
                                 isPartiallySubmitted(businessStatus);
  const hasProductSpecificationRequested = workflowStatus.includes('product_specification_requested');
  
  return isCorrectBusinessStatus && hasProductSpecificationRequested;
};

/**
 * Determine the new business status based on workflow status combinations after Share for Review
 * @param currentWorkflowStatus - The current workflow status array
 * @param updatedWorkflowStatus - The updated workflow status array after Share for Review
 * @returns The new business status to set
 */
export const getBusinessStatusAfterShareForReview = (currentWorkflowStatus: string[], updatedWorkflowStatus: string[]): string => {
  const hasComplianceDocumentRequested = currentWorkflowStatus.includes('compliance_document_requested');
  const hasComplianceDocumentUploaded = updatedWorkflowStatus.includes('compliance_document_uploaded');
  const hasProductSpecificationRequested = currentWorkflowStatus.includes('product_specification_requested');
  const hasProductSpecificationUploaded = updatedWorkflowStatus.includes('product_specification_uploaded');

  // Case 1: Both compliance_document_uploaded and product_specification_uploaded
  if (hasComplianceDocumentUploaded && hasProductSpecificationUploaded) {
    return 'under_product_developer_review';
  }

  // Case 2: compliance_document_uploaded and product_specification_requested
  if (hasComplianceDocumentUploaded && hasProductSpecificationRequested) {
    return 'partially_submitted';
  }

  // Case 3: compliance_document_requested and product_specification_uploaded
  if (hasComplianceDocumentRequested && hasProductSpecificationUploaded) {
    return 'partially_submitted';
  }

  // Case 4: compliance_document_requested and product_specification_requested
  if (hasComplianceDocumentRequested && hasProductSpecificationRequested) {
    return 'partially_submitted';
  }

  // Default: keep current status
  return 'partially_submitted';
};

/**
 * Get updated workflow statuses when both documents are uploaded
 * @param currentWorkflowStatus - The current workflow status array
 * @param updatedWorkflowStatus - The updated workflow status array after Share for Review
 * @returns Array of workflow statuses to set (replaces existing workflow status)
 */
export const getUpdatedWorkflowStatuses = (currentWorkflowStatus: string[], updatedWorkflowStatus: string[]): string[] => {
  const hasComplianceDocumentUploaded = updatedWorkflowStatus.includes('compliance_document_uploaded');
  const hasProductSpecificationUploaded = updatedWorkflowStatus.includes('product_specification_uploaded');

  // When both documents are uploaded, replace workflow status with review statuses
  if (hasComplianceDocumentUploaded && hasProductSpecificationUploaded) {
    return ['ps_compliance_document_review', 'ps_main_data_review'];
  }

  // Otherwise, return the updated workflow status as-is
  return updatedWorkflowStatus;
};

/**
 * Check if workflow status has both compliance and main details reviews complete
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if both reviews are complete
 */
export const hasBothReviewsComplete = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_compliance_document_review_complete') && 
         workflowStatus.includes('ps_main_details_review_complete');
};

/**
 * Get business status after both reviews are complete
 * @param currentWorkflowStatus - The current workflow status array
 * @param updatedWorkflowStatus - The updated workflow status array
 * @returns The new business status
 */
export const getBusinessStatusAfterBothReviewsComplete = (currentWorkflowStatus: string[], updatedWorkflowStatus: string[]): string => {
  const hasBothComplete = hasBothReviewsComplete(updatedWorkflowStatus);
  
  if (hasBothComplete) {
    return 'under_internal_review';
  }
  
  // Default: keep current status
  return 'under_product_developer_review';
};

/**
 * Get workflow statuses for internal review phase
 * @returns Array of workflow statuses for internal review
 */
export const getInternalReviewWorkflowStatuses = (): string[] => {
  return [
    'ps_nt_review_recipe',
    'ps_nt_review_raw_materials',
    'ps_nt_review_allergens_and_sensitivities',
    'ps_nt_review_nutrition',
    'ps_nt_review_packaging_and_labelling',
    'ps_nt_review_nutrition_pack_copy',
    'ps_qa_review_food_safety_qa',
    'ps_tm_review_storage',
    'ps_tm_review_packaging_and_labelling'
  ];
};

/**
 * Get updated workflow statuses when both reviews are complete
 * @param currentWorkflowStatus - The current workflow status array
 * @param updatedWorkflowStatus - The updated workflow status array
 * @returns Array of workflow statuses to set (replaces existing workflow status)
 */
export const getWorkflowStatusesAfterBothReviewsComplete = (currentWorkflowStatus: string[], updatedWorkflowStatus: string[]): string[] => {
  const hasBothComplete = hasBothReviewsComplete(updatedWorkflowStatus);
  
  if (hasBothComplete) {
    return getInternalReviewWorkflowStatuses();
  }
  
  // Otherwise, return the updated workflow status as-is
  return updatedWorkflowStatus;
};

/**
 * Get complete workflow status transition data when both reviews are complete
 * @param currentWorkflowStatus - The current workflow status array
 * @param updatedWorkflowStatus - The updated workflow status array
 * @returns Object containing business status and workflow statuses
 */
export const getCompleteWorkflowTransition = (currentWorkflowStatus: string[], updatedWorkflowStatus: string[]): {
  businessStatus: string;
  workflowStatus: string[];
} => {
  const hasBothComplete = hasBothReviewsComplete(updatedWorkflowStatus);
  
  if (hasBothComplete) {
    return {
      businessStatus: 'under_internal_review',
      workflowStatus: getInternalReviewWorkflowStatuses()
    };
  }
  
  // Default: keep current status and workflow
  return {
    businessStatus: 'under_product_developer_review',
    workflowStatus: updatedWorkflowStatus
  };
};

/**
 * Check if user has required role for internal review buttons (Nutritionist or Business Admin)
 * @param userRoles - Array of user roles
 * @returns boolean indicating if user has required role
 */
export const hasInternalReviewRole = (userRoles: any[]): boolean => {
  return userRoles.some((role: any) => {
    const roleName = role?.rolename?.toLowerCase();
    return roleName === 'nutritionist' || roleName === 'business admin';
  });
};

/**
 * Check if workflow status has recipe review status
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if recipe review status is present
 */
export const hasRecipeReviewStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_nt_review_recipe');
};

/**
 * Check if review complete and request for revision buttons should be shown for recipe tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns boolean indicating if buttons should be shown
 */
export const shouldShowRecipeReviewButtons = (businessStatus: string, workflowStatus: string[], userRoles: any[]): boolean => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasRecipeReview = hasRecipeReviewStatus(workflowStatus);
  const hasRequiredRole = hasInternalReviewRole(userRoles);
  
  return isUnderInternalReview && hasRecipeReview && hasRequiredRole;
};

/**
 * Check if workflow status has raw materials review status
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if raw materials review status is present
 */
export const hasRawMaterialsReviewStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_nt_review_raw_materials');
};

/**
 * Check if review complete and request for revision buttons should be shown for raw materials tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns boolean indicating if buttons should be shown
 */
export const shouldShowRawMaterialsReviewButtons = (businessStatus: string, workflowStatus: string[], userRoles: any[]): boolean => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasRawMaterialsReview = hasRawMaterialsReviewStatus(workflowStatus);
  const hasRequiredRole = hasInternalReviewRole(userRoles);
  
  return isUnderInternalReview && hasRawMaterialsReview && hasRequiredRole;
};

/**
 * Check if workflow status has allergens and sensitivities review status
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if allergens and sensitivities review status is present
 */
export const hasAllergensAndSensitivitiesReviewStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_nt_review_allergens_and_sensitivities');
};

/**
 * Check if review complete and request for revision buttons should be shown for allergens and sensitivities tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns boolean indicating if buttons should be shown
 */
export const shouldShowAllergensAndSensitivitiesReviewButtons = (businessStatus: string, workflowStatus: string[], userRoles: any[]): boolean => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasAllergensAndSensitivitiesReview = hasAllergensAndSensitivitiesReviewStatus(workflowStatus);
  const hasRequiredRole = hasInternalReviewRole(userRoles);
  
  return isUnderInternalReview && hasAllergensAndSensitivitiesReview && hasRequiredRole;
};

/**
 * Check if workflow status has nutrition review status
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if nutrition review status is present
 */
export const hasNutritionReviewStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_nt_review_nutrition');
};

/**
 * Check if review complete and request for revision buttons should be shown for nutrition tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns boolean indicating if buttons should be shown
 */
export const shouldShowNutritionReviewButtons = (businessStatus: string, workflowStatus: string[], userRoles: any[]): boolean => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasNutritionReview = hasNutritionReviewStatus(workflowStatus);
  const hasRequiredRole = hasInternalReviewRole(userRoles);
  
  return isUnderInternalReview && hasNutritionReview && hasRequiredRole;
};

/**
 * Check if workflow status has nutrition pack copy review status
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if nutrition pack copy review status is present
 */
export const hasNutritionPackCopyReviewStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_nt_review_nutrition_pack_copy');
};

/**
 * Check if review complete and request for revision buttons should be shown for nutrition declaration tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns boolean indicating if buttons should be shown
 */
export const shouldShowNutritionDeclarationReviewButtons = (businessStatus: string, workflowStatus: string[], userRoles: any[]): boolean => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasNutritionPackCopyReview = hasNutritionPackCopyReviewStatus(workflowStatus);
  const hasRequiredRole = hasInternalReviewRole(userRoles);
  
  return isUnderInternalReview && hasNutritionPackCopyReview && hasRequiredRole;
};

/**
 * Check if workflow status has packaging and labelling review status
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if packaging and labelling review status is present
 */
export const hasPackagingAndLabellingReviewStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_nt_review_packaging_and_labelling');
};

/**
 * Check if review complete and request for revision buttons should be shown for packaging and labelling tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns boolean indicating if buttons should be shown
 */
export const shouldShowPackagingAndLabellingReviewButtons = (businessStatus: string, workflowStatus: string[], userRoles: any[]): boolean => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasPackagingAndLabellingReview = hasPackagingAndLabellingReviewStatus(workflowStatus);
  const hasRequiredRole = hasInternalReviewRole(userRoles);
  
  return isUnderInternalReview && hasPackagingAndLabellingReview && hasRequiredRole;
};

/**
 * Check if workflow status has storage review status
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if storage review status is present
 */
export const hasStorageReviewStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_tm_review_storage');
};

/**
 * Check if user has traffic manager or business admin role
 * @param userRoles - Array of user roles
 * @returns boolean indicating if user has required role
 */
export const hasTrafficManagerOrBusinessAdminRole = (userRoles: any[]): boolean => {
  return userRoles.some((role: any) => {
    const roleName = role?.rolename?.toLowerCase();
    return roleName === 'traffic manager' || roleName === 'business admin';
  });
};

/**
 * Check if review complete and request for revision buttons should be shown for storage tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns boolean indicating if buttons should be shown
 */
export const shouldShowStorageReviewButtons = (businessStatus: string, workflowStatus: string[], userRoles: any[]): boolean => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasStorageReview = hasStorageReviewStatus(workflowStatus);
  const hasRequiredRole = hasTrafficManagerOrBusinessAdminRole(userRoles);
  
  return isUnderInternalReview && hasStorageReview && hasRequiredRole;
};

/**
 * Check if workflow status has packaging and labelling review status (Traffic Manager version)
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if packaging and labelling review status is present
 */
export const hasPackagingAndLabellingTMReviewStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_tm_review_packaging_and_labelling');
};

/**
 * Check if review complete and request for revision buttons should be shown for packaging and labelling tab (Traffic Manager version)
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns boolean indicating if buttons should be shown
 */
export const shouldShowPackagingAndLabellingTMReviewButtons = (businessStatus: string, workflowStatus: string[], userRoles: any[]): boolean => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasPackagingAndLabellingTMReview = hasPackagingAndLabellingTMReviewStatus(workflowStatus);
  const hasRequiredRole = hasTrafficManagerOrBusinessAdminRole(userRoles);
  
  return isUnderInternalReview && hasPackagingAndLabellingTMReview && hasRequiredRole;
};

/**
 * Check if workflow status has food safety review status
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if food safety review status is present
 */
export const hasFoodSafetyReviewStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_qa_review_food_safety_qa');
};

/**
 * Check if user has food safety or business admin role
 * @param userRoles - Array of user roles
 * @returns boolean indicating if user has required role
 */
export const hasFoodSafetyOrBusinessAdminRole = (userRoles: any[]): boolean => {
  return userRoles.some((role: any) => {
    const roleName = role?.rolename?.toLowerCase();
    return roleName === 'food safety team' || roleName === 'business admin';
  });
};

/**
 * Check if review complete and request for revision buttons should be shown for food safety tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns boolean indicating if buttons should be shown
 */
export const shouldShowFoodSafetyReviewButtons = (availableTransitions: AvailableTransitions): boolean => {
  // const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  // const hasFoodSafetyReview = hasFoodSafetyReviewStatus(workflowStatus);

  // const hasRequiredRole = hasFoodSafetyOrBusinessAdminRole(userRoles);
  const transitionIndex = availableTransitions?.data?.length > 0 ? availableTransitions?.data?.findIndex(t => t.label === transitions.active_projects.qa_request_revision_food_safety_qa) : -1;

  return transitionIndex > -1;
};

/**
 * Check if food safety review is complete
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if food safety review is complete
 */
export const hasFoodSafetyReviewCompleteStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_qa_review_complete_food_safety_qa');
};

/**
 * Get updated workflow status after food safety review complete
 * @param currentWorkflowStatus - The current workflow status array
 * @returns updated workflow status array with food safety review marked complete
 */
export const getWorkflowStatusAfterFoodSafetyReviewComplete = (currentWorkflowStatus: string[]): string[] => {
  return currentWorkflowStatus.map(status => 
    status === 'ps_qa_review_food_safety_qa' ? 'ps_qa_review_complete_food_safety_qa' : status
  );
};

/**
 * Check if review complete button should be shown and disabled for food safety tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns object with show and disabled flags
 */
export const getFoodSafetyReviewCompleteButtonState = (workflowStatus: string[], availableTransition: AvailableTransitions): { show: boolean; disabled: boolean } => {
  // const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  // const hasFoodSafetyReview = hasFoodSafetyReviewStatus(workflowStatus);
  const hasFoodSafetyReviewComplete = hasFoodSafetyReviewCompleteStatus(workflowStatus);
  // Check for either qa_revision_review_food_safety_qa (after revision) or qa_review_complete_food_safety_qa (initial review)
  const hasRevisionReviewTransition = availableTransition?.data?.length > 0 ? availableTransition.data.findIndex(t => t.label === transitions.active_projects.qa_revision_review_food_safety_qa) !== -1 : false;
  const hasReviewCompleteTransition = availableTransition?.data?.length > 0 ? availableTransition.data.findIndex(t => t.label === transitions.active_projects.qa_review_complete_food_safety_qa) !== -1 : false;
  const shouldShow = hasRevisionReviewTransition || hasReviewCompleteTransition;
  // const hasRequiredRole = hasFoodSafetyOrBusinessAdminRole(userRoles);
  
  // const shouldShow = isUnderInternalReview && hasFoodSafetyReview && hasRequiredRole;
  const shouldDisable = hasFoodSafetyReviewComplete;
  
  return {
    show: shouldShow,
    disabled: shouldDisable
  };
};

/**
 * Check if storage review is complete
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if storage review is complete
 */
export const hasStorageReviewCompleteStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_tm_review_complete_storage');
};

/**
 * Get updated workflow status after storage review complete
 * @param currentWorkflowStatus - The current workflow status array
 * @returns updated workflow status array with storage review marked complete
 */
export const getWorkflowStatusAfterStorageReviewComplete = (currentWorkflowStatus: string[]): string[] => {
  return currentWorkflowStatus.map(status => 
    status === 'ps_tm_review_storage' ? 'ps_tm_review_complete_storage' : status
  );
};

/**
 * Check if review complete button should be shown and disabled for storage tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns object with show and disabled flags
 */
export const getStorageReviewCompleteButtonState = (businessStatus: string, workflowStatus: string[], userRoles: any[]): { show: boolean; disabled: boolean } => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasStorageReview = hasStorageReviewStatus(workflowStatus);
  const hasStorageReviewComplete = hasStorageReviewCompleteStatus(workflowStatus);
  const hasRequiredRole = hasTrafficManagerOrBusinessAdminRole(userRoles);
  
  const shouldShow = isUnderInternalReview && hasStorageReview && hasRequiredRole;
  const shouldDisable = hasStorageReviewComplete;
  
  return {
    show: shouldShow,
    disabled: shouldDisable
  };
};

/**
 * Check if packaging and labelling nutritionist review is complete
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if nutritionist packaging review is complete
 */
export const hasPackagingAndLabellingNutritionistReviewCompleteStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_nt_review_complete_packaging_and_labelling');
};

/**
 * Check if packaging and labelling traffic manager review is complete
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if traffic manager packaging review is complete
 */
export const hasPackagingAndLabellingTrafficManagerReviewCompleteStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_tm_review_complete_packaging_and_labelling');
};

/**
 * Get updated workflow status after packaging and labelling nutritionist review complete
 * @param currentWorkflowStatus - The current workflow status array
 * @returns updated workflow status array with nutritionist packaging review marked complete
 */
export const getWorkflowStatusAfterPackagingAndLabellingNutritionistReviewComplete = (currentWorkflowStatus: string[]): string[] => {
  return currentWorkflowStatus.map(status => 
    status === 'ps_nt_review_packaging_and_labelling' ? 'ps_nt_review_complete_packaging_and_labelling' : status
  );
};

/**
 * Get updated workflow status after packaging and labelling traffic manager review complete
 * @param currentWorkflowStatus - The current workflow status array
 * @returns updated workflow status array with traffic manager packaging review marked complete
 */
export const getWorkflowStatusAfterPackagingAndLabellingTrafficManagerReviewComplete = (currentWorkflowStatus: string[]): string[] => {
  return currentWorkflowStatus.map(status => 
    status === 'ps_tm_review_packaging_and_labelling' ? 'ps_tm_review_complete_packaging_and_labelling' : status
  );
};

/**
 * Check if review complete button should be shown and disabled for packaging and labelling tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns object with show and disabled flags
 */
export const getPackagingAndLabellingReviewCompleteButtonState = (businessStatus: string, workflowStatus: string[], userRoles: any[]): { show: boolean; disabled: boolean } => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasNutritionistRole = hasInternalReviewRole(userRoles); // This includes both Nutritionist and Business Admin
  const hasTrafficManagerRole = hasTrafficManagerOrBusinessAdminRole(userRoles);
  
  // Check if user has Business Admin role specifically
  const hasBusinessAdminRole = userRoles.some((role: any) => {
    const roleName = role?.rolename?.toLowerCase();
    return roleName === 'business admin';
  });
  
  // Check for nutritionist review status
  const hasNutritionistReview = hasPackagingAndLabellingReviewStatus(workflowStatus);
  const hasNutritionistReviewComplete = hasPackagingAndLabellingNutritionistReviewCompleteStatus(workflowStatus);
  
  // Check for traffic manager review status
  const hasTrafficManagerReview = hasPackagingAndLabellingTMReviewStatus(workflowStatus);
  const hasTrafficManagerReviewComplete = hasPackagingAndLabellingTrafficManagerReviewCompleteStatus(workflowStatus);
  
  // Determine which review the user can complete based on their role
  let canCompleteNutritionistReview = false;
  let canCompleteTrafficManagerReview = false;
  
  if (hasBusinessAdminRole) {
    // Business Admin can complete both reviews
    canCompleteNutritionistReview = hasNutritionistReview;
    canCompleteTrafficManagerReview = hasTrafficManagerReview;
  } else if (hasNutritionistRole && !hasTrafficManagerRole) {
    // Nutritionist can only complete their own review (not if they're also Traffic Manager)
    canCompleteNutritionistReview = hasNutritionistReview;
  } else if (hasTrafficManagerRole && !hasNutritionistRole) {
    // Traffic Manager can only complete their own review (not if they're also Nutritionist)
    canCompleteTrafficManagerReview = hasTrafficManagerReview;
  }
  
  const shouldShow = isUnderInternalReview && (canCompleteNutritionistReview || canCompleteTrafficManagerReview);
  const shouldDisable = (canCompleteNutritionistReview && hasNutritionistReviewComplete) || 
                       (canCompleteTrafficManagerReview && hasTrafficManagerReviewComplete);
  
  return {
    show: shouldShow,
    disabled: shouldDisable
  };
};

/**
 * Check if recipe review is complete
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if recipe review is complete
 */
export const hasRecipeReviewCompleteStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_nt_review_complete_recipe');
};

/**
 * Get updated workflow status after recipe review complete
 * @param currentWorkflowStatus - The current workflow status array
 * @returns updated workflow status array with recipe review marked complete
 */
export const getWorkflowStatusAfterRecipeReviewComplete = (currentWorkflowStatus: string[]): string[] => {
  return currentWorkflowStatus.map(status => 
    status === 'ps_nt_review_recipe' ? 'ps_nt_review_complete_recipe' : status
  );
};

/**
 * Check if review complete button should be shown and disabled for recipe tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns object with show and disabled flags
 */
export const getRecipeReviewCompleteButtonState = (businessStatus: string, workflowStatus: string[], userRoles: any[]): { show: boolean; disabled: boolean } => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasRecipeReview = hasRecipeReviewStatus(workflowStatus);
  const hasRecipeReviewComplete = hasRecipeReviewCompleteStatus(workflowStatus);
  const hasRequiredRole = hasInternalReviewRole(userRoles);
  
  const shouldShow = isUnderInternalReview && hasRecipeReview && hasRequiredRole;
  const shouldDisable = hasRecipeReviewComplete;
  
  return {
    show: shouldShow,
    disabled: shouldDisable
  };
};

/**
 * Check if raw materials review is complete
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if raw materials review is complete
 */
export const hasRawMaterialsReviewCompleteStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_nt_review_complete_raw_materials');
};

/**
 * Get updated workflow status after raw materials review complete
 * @param currentWorkflowStatus - The current workflow status array
 * @returns updated workflow status array with raw materials review marked complete
 */
export const getWorkflowStatusAfterRawMaterialsReviewComplete = (currentWorkflowStatus: string[]): string[] => {
  return currentWorkflowStatus.map(status => 
    status === 'ps_nt_review_raw_materials' ? 'ps_nt_review_complete_raw_materials' : status
  );
};

/**
 * Check if review complete button should be shown and disabled for raw materials tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns object with show and disabled flags
 */
export const getRawMaterialsReviewCompleteButtonState = (businessStatus: string, workflowStatus: string[], userRoles: any[]): { show: boolean; disabled: boolean } => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasRawMaterialsReview = hasRawMaterialsReviewStatus(workflowStatus);
  const hasRawMaterialsReviewComplete = hasRawMaterialsReviewCompleteStatus(workflowStatus);
  const hasRequiredRole = hasInternalReviewRole(userRoles);
  
  const shouldShow = isUnderInternalReview && hasRawMaterialsReview && hasRequiredRole;
  const shouldDisable = hasRawMaterialsReviewComplete;
  
  return {
    show: shouldShow,
    disabled: shouldDisable
  };
};

/**
 * Check if allergens and sensitivities review is complete
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if allergens and sensitivities review is complete
 */
export const hasAllergensAndSensitivitiesReviewCompleteStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_nt_review_complete_allergens_and_sensitivities');
};

/**
 * Get updated workflow status after allergens and sensitivities review complete
 * @param currentWorkflowStatus - The current workflow status array
 * @returns updated workflow status array with allergens and sensitivities review marked complete
 */
export const getWorkflowStatusAfterAllergensAndSensitivitiesReviewComplete = (currentWorkflowStatus: string[]): string[] => {
  return currentWorkflowStatus.map(status => 
    status === 'ps_nt_review_allergens_and_sensitivities' ? 'ps_nt_review_complete_allergens_and_sensitivities' : status
  );
};

/**
 * Check if review complete button should be shown and disabled for allergens and sensitivities tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns object with show and disabled flags
 */
export const getAllergensAndSensitivitiesReviewCompleteButtonState = (businessStatus: string, workflowStatus: string[], userRoles: any[]): { show: boolean; disabled: boolean } => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasAllergensAndSensitivitiesReview = hasAllergensAndSensitivitiesReviewStatus(workflowStatus);
  const hasAllergensAndSensitivitiesReviewComplete = hasAllergensAndSensitivitiesReviewCompleteStatus(workflowStatus);
  const hasRequiredRole = hasInternalReviewRole(userRoles);
  
  const shouldShow = isUnderInternalReview && hasAllergensAndSensitivitiesReview && hasRequiredRole;
  const shouldDisable = hasAllergensAndSensitivitiesReviewComplete;
  
  return {
    show: shouldShow,
    disabled: shouldDisable
  };
};

/**
 * Check if nutrition review is complete
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if nutrition review is complete
 */
export const hasNutritionReviewCompleteStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_nt_review_nutrition_complete');
};

/**
 * Get updated workflow status after nutrition review complete
 * @param currentWorkflowStatus - The current workflow status array
 * @returns updated workflow status array with nutrition review marked complete
 */
export const getWorkflowStatusAfterNutritionReviewComplete = (currentWorkflowStatus: string[]): string[] => {
  return currentWorkflowStatus.map(status => 
    status === 'ps_nt_review_nutrition' ? 'ps_nt_review_nutrition_complete' : status
  );
};

/**
 * Check if review complete button should be shown and disabled for nutrition tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns object with show and disabled flags
 */
export const getNutritionReviewCompleteButtonState = (businessStatus: string, workflowStatus: string[], userRoles: any[]): { show: boolean; disabled: boolean } => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasNutritionReview = hasNutritionReviewStatus(workflowStatus);
  const hasNutritionReviewComplete = hasNutritionReviewCompleteStatus(workflowStatus);
  const hasRequiredRole = hasInternalReviewRole(userRoles);
  
  const shouldShow = isUnderInternalReview && hasNutritionReview && hasRequiredRole;
  const shouldDisable = hasNutritionReviewComplete;
  
  return {
    show: shouldShow,
    disabled: shouldDisable
  };
};

/**
 * Check if nutrition declaration review is complete
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if nutrition declaration review is complete
 */
export const hasNutritionDeclarationReviewCompleteStatus = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_nt_review_nutrition_pack_copy_complete');
};

/**
 * Get updated workflow status after nutrition declaration review complete
 * @param currentWorkflowStatus - The current workflow status array
 * @returns updated workflow status array with nutrition declaration review marked complete
 */
export const getWorkflowStatusAfterNutritionDeclarationReviewComplete = (currentWorkflowStatus: string[]): string[] => {
  return currentWorkflowStatus.map(status => 
    status === 'ps_nt_review_nutrition_pack_copy' ? 'ps_nt_review_nutrition_pack_copy_complete' : status
  );
};

/**
 * Get updated workflow status after HABA recipe or raw materials review complete
 * For HABA products, completing either recipe or raw materials review completes both
 * @param currentWorkflowStatus - The current workflow status array
 * @returns updated workflow status array with both recipe and raw materials reviews marked complete
 */
export const getWorkflowStatusAfterHABAReviewComplete = (currentWorkflowStatus: string[]): string[] => {
  return currentWorkflowStatus.map(status => {
    if (status === 'ps_nt_review_recipe') {
      return 'ps_nt_review_complete_recipe';
    } else if (status === 'ps_nt_review_raw_materials') {
      return 'ps_nt_review_complete_raw_materials';
    }
    return status;
  });
};

/**
 * Check if HABA recipe review complete button should be shown and disabled
 * For HABA products, the button should be disabled if either recipe or raw materials review is complete
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns object with show and disabled flags
 */
export const getHABARecipeReviewCompleteButtonState = (businessStatus: string, workflowStatus: string[], userRoles: any[]): { show: boolean; disabled: boolean } => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasRecipeReview = hasRecipeReviewStatus(workflowStatus);
  const hasRequiredRole = hasInternalReviewRole(userRoles);
  
  // For HABA: check if either recipe or raw materials review is complete
  const hasRecipeReviewComplete = hasRecipeReviewCompleteStatus(workflowStatus);
  const hasRawMaterialsReviewComplete = hasRawMaterialsReviewCompleteStatus(workflowStatus);
  
  const shouldShow = isUnderInternalReview && hasRecipeReview && hasRequiredRole;
  const shouldDisable = hasRecipeReviewComplete || hasRawMaterialsReviewComplete;
  
  return {
    show: shouldShow,
    disabled: shouldDisable
  };
};

/**
 * Check if HABA raw materials review complete button should be shown and disabled
 * For HABA products, the button should be disabled if either recipe or raw materials review is complete
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns object with show and disabled flags
 */
export const getHABARawMaterialsReviewCompleteButtonState = (businessStatus: string, workflowStatus: string[], userRoles: any[]): { show: boolean; disabled: boolean } => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasRawMaterialsReview = hasRawMaterialsReviewStatus(workflowStatus);
  const hasRequiredRole = hasInternalReviewRole(userRoles);
  
  // For HABA: check if either recipe or raw materials review is complete
  const hasRecipeReviewComplete = hasRecipeReviewCompleteStatus(workflowStatus);
  const hasRawMaterialsReviewComplete = hasRawMaterialsReviewCompleteStatus(workflowStatus);
  
  const shouldShow = isUnderInternalReview && hasRawMaterialsReview && hasRequiredRole;
  const shouldDisable = hasRecipeReviewComplete || hasRawMaterialsReviewComplete;
  
  return {
    show: shouldShow,
    disabled: shouldDisable
  };
};

/**
 * Check if review complete button should be shown and disabled for nutrition declaration tab
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns object with show and disabled flags
 */
export const getNutritionDeclarationReviewCompleteButtonState = (businessStatus: string, workflowStatus: string[], userRoles: any[]): { show: boolean; disabled: boolean } => {
  const isUnderInternalReview = normalizeStatusForComparison(businessStatus) === 'under_internal_review';
  const hasNutritionDeclarationReview = hasNutritionPackCopyReviewStatus(workflowStatus);
  const hasNutritionDeclarationReviewComplete = hasNutritionDeclarationReviewCompleteStatus(workflowStatus);
  const hasRequiredRole = hasInternalReviewRole(userRoles);
  
  const shouldShow = isUnderInternalReview && hasNutritionDeclarationReview && hasRequiredRole;
  const shouldDisable = hasNutritionDeclarationReviewComplete;
  
  return {
    show: shouldShow,
    disabled: shouldDisable
  };
};

/**
 * Check if a status is under product developer review (case-insensitive with space-to-underscore conversion)
 * @param status - The status to check
 * @returns boolean indicating if the status is under product developer review
 */
export const isUnderProductDeveloperReviewStatus = (status: string): boolean => {
  const normalizedStatus = normalizeStatusForComparison(status);
  const underProductDeveloperReview = normalizeStatusForComparison(UNDER_PRODUCT_DEVELOPER_REVIEW);
  return normalizedStatus === underProductDeveloperReview;
};

/**
 * True when vendor assessment is in the "Final Specification Review and Acknowledgement - Vendor" phase
 * (normalized match to ACTIVE_PROJECT_STATUSES or the display label).
 */
export const isVendorAssessmentFinalSpecificationReviewAcknowledgementVendor = (status?: string | null): boolean => {
  if (!status || typeof status !== 'string') {
    return false;
  }
  const normalized = normalizeStatusForComparison(status);
  const slugNorm = normalizeStatusForComparison(ACTIVE_PROJECT_STATUSES.FINAL_SPECIFICATION_REVIEW_AND_ACKNOWLEDGEMENT_VENDOR);
  const displayNorm = normalizeStatusForComparison('Final Specification Review and Acknowledgement - Vendor');
  return normalized === slugNorm || normalized === displayNorm;
};

/**
 * True when `status` matches a canonical active-project status value from
 * {@link ACTIVE_PROJECT_STATUSES} (or the same string from API), after
 * {@link normalizeStatusForComparison}: exact match, or composite / status-bar
 * strings whose normalized form contains the canonical slug.
 *
 * @param status - Runtime status (e.g. business or vendor assessment status)
 * @param canonicalStatus - Constant value, e.g. `ACTIVE_PROJECT_STATUSES.ARTWORK_IN_DESIGN`
 */
export const matchesActiveProjectCanonicalStatus = (
  status: string | null | undefined,
  canonicalStatus: string,
): boolean => {
  if (!status || typeof status !== 'string') {
    return false;
  }
  const normalized = normalizeStatusForComparison(status);
  const canonicalNorm = normalizeStatusForComparison(canonicalStatus);
  if (!canonicalNorm) {
    return false;
  }
  return normalized === canonicalNorm || normalized.includes(canonicalNorm);
};

/**
 * True when vendor assessment / business status is Artwork In-Design.
 * @see {@link matchesActiveProjectCanonicalStatus}
 */
export const isArtworkInDesignStatus = (status?: string | null): boolean =>
  matchesActiveProjectCanonicalStatus(status, ACTIVE_PROJECT_STATUSES.ARTWORK_IN_DESIGN);

/**
 * Statuses from "Under Product Developer Review" onwards (vendor can see Food Safety tab from this point).
 * Normalized form used for comparison. Final-spec TJ phase is expected as API slug `final_specification_review_and_approval_tj` only.
 */
const STATUSES_FROM_PRODUCT_DEVELOPER_REVIEW_ONWARDS = new Set([
  'under_product_developer_review',
  'under_internal_review',
  'under_legal_review',
  'artwork_in_design',
  'final_specification_review_and_acknowledgement_vendor',
  'final_specification_review_and_approval_tj',
  'ready_for_orders',
  'prepare_for_launch',
  'superseded',
]);

/**
 * Check if business status is at or after "Under Product Developer Review" (vendor Food Safety tab visibility).
 * @param status - The business status to check (e.g. from selectedActiveProject.businessStatus)
 * @returns true if status is Under Product Developer Review or any later status in the workflow
 */
export const isStatusFromProductDeveloperReviewOnwards = (status: string | null | undefined): boolean => {
  if (!status || typeof status !== 'string') {
    return false;
  }
  const normalized = normalizeStatusForComparison(status);
  return STATUSES_FROM_PRODUCT_DEVELOPER_REVIEW_ONWARDS.has(normalized);
};

const ACTIVE_PROJECT_WORKFLOW_RANGE_NORMALIZED = new Set(
  Object.values(ACTIVE_PROJECT_STATUSES_IN_WORKFLOW_RANGE).map(normalizeStatusForComparison),
);

/** Maps display labels with "&" to slug form before comparison (e.g. "Review & Approval" → "review_and_approval"). */
const normalizeForWorkflowRangeComparison = (status: string): string =>
  normalizeStatusForComparison(status.replace(/\s*&\s*/g, ' and '));

/**
 * True when status is in the Active Projects workflow range from Partially Submitted
 * through Final Specification Review & Approval - TJ (inclusive). Exact normalized match only.
 */
export const isActiveProjectStatusInWorkflowRange = (
  status: string | null | undefined,
): boolean => {
  if (!status || typeof status !== 'string') {
    return false;
  }
  return ACTIVE_PROJECT_WORKFLOW_RANGE_NORMALIZED.has(normalizeForWorkflowRangeComparison(status));
};

/**
 * True when the Not Progressed stepper step should appear: assessment flagged notProgressed
 * and vendor/business status is in the Active Projects workflow range.
 */
export function shouldShowNotProgressedStepperStep(
  notProgressed: unknown,
  status: string | null | undefined,
): boolean {
  if (notProgressed === true) {
    return isActiveProjectStatusInWorkflowRange(status);
  }
  if (notProgressed === false || notProgressed == null) {
    return false;
  }
  if (typeof notProgressed === 'string') {
    return notProgressed.trim().toLowerCase() === 'true' && isActiveProjectStatusInWorkflowRange(status);
  }
  return false;
}

/**
 * Check if business status is generate_nt_pack_copy
 * @param status - The status to check
 * @returns boolean indicating if the status is generate_nt_pack_copy
 */
export const isGenerateNtPackCopyStatus = (status: string | undefined | null): boolean => {
  if (!status) {
    return false;
  }
  const normalizedStatus = normalizeStatusForComparison(status);
  return normalizedStatus === 'generate_nt_pack_copy';
};

/**
 * Check if workflow status has both compliance_document_uploaded and product_specification_uploaded
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if both documents are uploaded
 */
export const hasBothDocumentsUploaded = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('compliance_document_uploaded') && 
         workflowStatus.includes('product_specification_uploaded');
};

/**
 * Check if workflow status has both review statuses for compliance documents
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if both review statuses are present
 */
export const hasBothReviewStatuses = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_compliance_document_review') && 
         workflowStatus.includes('ps_main_data_review');
};

/**
 * Check if workflow status has compliance document review and main details review complete statuses
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if both required statuses are present
 */
export const hasComplianceReviewAndMainDetailsComplete = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_compliance_document_review') && 
         workflowStatus.includes('ps_main_details_review_complete');
};

/**
 * Check if user has required role for review complete and request for revision buttons
 * @param userRoles - Array of user roles
 * @returns boolean indicating if user has required role
 */
export const hasReviewRole = (userRoles: any[]): boolean => {
  return userRoles.some((role: any) => {
    const roleName = role?.rolename?.toLowerCase();
    return roleName === 'product creator' ||
           roleName === 'category manager' ||
           roleName === 'project manager' ||
           roleName === 'system excellence manager' ||
           roleName === 'business admin';
  });
};

/**
 * Check if review complete and request for revision buttons should be shown
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns boolean indicating if buttons should be shown
 */
export const shouldShowReviewButtons = (businessStatus: string, workflowStatus: string[], userRoles: any[]): boolean => {
  const isCorrectBusinessStatus = isUnderProductDeveloperReviewStatus(businessStatus);
  const hasBothUploaded = hasBothDocumentsUploaded(workflowStatus);
  const hasRequiredRole = hasReviewRole(userRoles);
  
  return isCorrectBusinessStatus && hasBothUploaded && hasRequiredRole;
};

/**
 * Check if review complete and request for revision buttons should be shown for compliance documents
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns boolean indicating if buttons should be shown
 */
export const shouldShowComplianceReviewButtons = (businessStatus: string, workflowStatus: string[], userRoles: any[]): boolean => {
  const isCorrectBusinessStatus = isUnderProductDeveloperReviewStatus(businessStatus);
  const hasComplianceReviewAndMainDataReview = workflowStatus.includes('ps_compliance_document_review') && 
                                              workflowStatus.includes('ps_main_data_review');
  const hasRequiredRole = hasReviewRole(userRoles);
  
  return isCorrectBusinessStatus && hasComplianceReviewAndMainDataReview && hasRequiredRole;
};

/**
 * Check if workflow status has compliance document review and main data review statuses
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if both required statuses are present
 */
export const hasComplianceReviewAndMainDataReview = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_compliance_document_review') && 
         workflowStatus.includes('ps_main_data_review');
};

/**
 * Check if workflow status has compliance document review complete and main data review statuses
 * @param workflowStatus - The workflow status array to check
 * @returns boolean indicating if both required statuses are present
 */
export const hasComplianceReviewCompleteAndMainDataReview = (workflowStatus: string[]): boolean => {
  return workflowStatus.includes('ps_compliance_document_review_complete') && 
         workflowStatus.includes('ps_main_data_review');
};

/**
 * Check if review complete and request for revision buttons should be shown for main details
 * @param businessStatus - The business status to check
 * @param workflowStatus - The workflow status array to check
 * @param userRoles - Array of user roles
 * @returns boolean indicating if buttons should be shown
 */
export const shouldShowMainDetailsReviewButtons = (businessStatus: string, workflowStatus: string[], userRoles: any[]): boolean => {
  const isCorrectBusinessStatus = isUnderProductDeveloperReviewStatus(businessStatus);
  const hasComplianceReviewCompleteAndMainDataReview = workflowStatus.includes('ps_compliance_document_review_complete') && 
                                                      workflowStatus.includes('ps_main_data_review');
  const hasRequiredRole = hasReviewRole(userRoles);
  
  return isCorrectBusinessStatus && hasComplianceReviewCompleteAndMainDataReview && hasRequiredRole;
};

/**
 * Compute internal review sub-statuses from workflow statuses
 * This is a shared utility function used by InternalReviewStatusCell and DataGridCellNestedTooltip
 * @param workflowStatuses - Array of workflow status strings
 * @param internalReviewLabels - Object containing labels for sub-statuses
 * @returns Array of InternalReviewItem objects
 */
export const computeInternalReviewSubStatuses = (
  workflowStatuses: string[],
  internalReviewLabels: Record<string, string> = {},
  productSpecificationType?: string
): InternalReviewItem[] => {
  if (!Array.isArray(workflowStatuses) || workflowStatuses.length === 0) {
    return [];
  }
  
  const lower = workflowStatuses
    .filter((s: any) => typeof s === 'string' && s.trim())
    .map((s: string) => s.toLowerCase());
  
  // New simplified internal review workflow statuses
  // Nutritionist group
  const ntBase = ['ps_nt_review', 'ps_nt_request_revision'];
  const ntComplete = ['ps_nt_review_complete', 'generated_nt_pack_copy'];
  const hasNtGroup = ntBase.some(s => lower.includes(s)) || ntComplete.some(s => lower.includes(s));
  // Mark "done" if any completion marker is present (ps_all_review_completed override below)
  const ntDone = ntComplete.some(s => lower.includes(s));
  
  // Traffic Manager group
  const tmBase = ['ps_tm_review', 'ps_tm_request_revision'];
  const tmComplete = ['ps_tm_review_complete', 'generated_nt_pack_copy'];
  const hasTmGroup = tmBase.some(s => lower.includes(s)) || tmComplete.some(s => lower.includes(s));
  // Mark "done" if any completion marker is present (ps_all_review_completed override below)
  const tmDone = tmComplete.some(s => lower.includes(s));
  
  // Food Safety QA group
  const fsBase = ['ps_qa_review_food_safety_qa', 'ps_qa_request_revision_food_safety_qa'];
  const fsComplete = ['ps_qa_review_complete_food_safety_qa', 'generated_nt_pack_copy'];
  const hasFsGroup = fsBase.some(s => lower.includes(s)) || fsComplete.some(s => lower.includes(s));
  // Mark "done" if any completion marker is present (ps_all_review_completed override below)
  const fsDone = fsComplete.some(s => lower.includes(s));
  
  // Legal Review group
  const glcBase = ['ps_glc_review'];
  const glcComplete = ['ps_glc_reviewed'];
  const hasGlcGroup = glcBase.some(s => lower.includes(s)) || glcComplete.some(s => lower.includes(s));
  const glcDone = glcComplete.some(s => lower.includes(s));
  
  // Check if all reviews are completed
  const hasAllReviewCompleted = lower.includes('ps_all_review_completed');
  
  // Check if product is "All Branded Products" type (used for special-case display logic)
  const productType = String(productSpecificationType || '').toLowerCase();
  const isAllBrandedProducts =
    productType === 'all branded products' ||
    productType === 'all branded' ||
    productType.includes('all branded');
  
  // Special case: If ps_all_review_completed and product is All Branded Products,
  // show only Legal Review and Food Safety Review, both marked as completed
  const isAllBrandedSpecialCase = hasAllReviewCompleted && isAllBrandedProducts;
  
  const finalNtDone = hasAllReviewCompleted ? true : ntDone;
  const finalTmDone = hasAllReviewCompleted ? true : tmDone;
  const finalFsDone = hasAllReviewCompleted ? true : fsDone;
  
  const finalHasNtGroup = isAllBrandedSpecialCase ? false : (hasAllReviewCompleted ? true : hasNtGroup);
  const finalHasTmGroup = isAllBrandedSpecialCase ? false : (hasAllReviewCompleted ? true : hasTmGroup);
  const finalHasFsGroup = isAllBrandedSpecialCase ? true : (hasAllReviewCompleted ? true : hasFsGroup);
  const finalHasGlcGroup = isAllBrandedSpecialCase ? true : hasGlcGroup;
  const finalGlcDone = isAllBrandedSpecialCase ? true : glcDone;
  
  const items: InternalReviewItem[] = [];
  if (finalHasNtGroup) items.push({ label: internalReviewLabels.UNDER_NUTRITION_REVIEW || 'Under Nutrition Review', done: finalNtDone });
  if (finalHasTmGroup) items.push({ label: internalReviewLabels.UNDER_TRAFFIC_MANAGER_REVIEW || 'Under Traffic Manager Review', done: finalTmDone });
  if (finalHasFsGroup) items.push({ label: internalReviewLabels.UNDER_FOOD_SAFETY_REVIEW || 'Under Food Safety Review', done: finalFsDone });
  if (finalHasGlcGroup) items.push({ label: internalReviewLabels.UNDER_LEGAL_REVIEW || 'Under Legal Review', done: finalGlcDone });
  
  return items;
};